import { TextField, WithStyles, Typography, Divider, Button } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import { PaginatedDataDTO } from '@bottega52/commons-pagination';
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ICylinderInDTO } from '../../repository/jago/model/input/ICylinderInDTO';
import { IKeyInDTO } from '../../repository/jago/model/input/IKeyInDTO';
import { IMechanicalCylinderKeyRelationInDTO } from '../../repository/jago/model/input/IMechanicalCylinderKeyRelationInDTO';
import { IMechanicalCylinderKeyRelationsRequestParamsDTO } from '../../repository/jago/model/output/RequestParamsDTOs';
import { IState } from '../../redux/store';
import * as MksActions from '../../redux/mks/mks.actions';
import * as ModalsActions from '../../redux/modals/modals.actions';
import * as CreditsActions from '../../redux/credits/credits.actions';
import { ModalTypes } from '../Modals/ModalTypes';
import MksKeyForm from './MksKeyForm';
import AssociatedKeysTable from '../MKS/AssociatedKeysTable';
import ReduxLanguage from '../ReduxLanguage';
import { getDomainsOptions } from '../../repository/utils';
import { IDomainOptionDTO } from '../../repository/jago/model/input/IDomainOptionDTO';
import { MKSRequestTypeOptions, MKSRequestStatusOptions } from '../MKS/MKSRequestsTable';
import MKSRequestForm from './MKSRequestForm';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS } from '../../services/PermissionService/PermissionConstants';
import { IWalletPlantsRequestParamsDTO } from '../../repository/jago/model/output/RequestParamsDTOs';
import { IMKSRequestStatusChangeDTO } from '../../repository/jago/model/output/IMKSRequestDTO';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: 20,
    gap: 10
  },
  formField: {
    marginBottom: 10
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: 0,
    fontWeight: 'bold',
  },
  relationsList: {
    maxHeight: 300,
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
  },
  actionButton: {
    width: 'fit-content',
    color: 'white',
    marginLeft: 20,
    padding: 4,
    paddingLeft: 10,
    paddingRight: 10,
    fontWeight: 'bold'
  },
  actionButtonText: {
    margin: 0,
    fontWeight: 'bold',
  },
  icon: {
    fontSize: 15,
    marginRight: 5,
  },
});

function mapStateToProps(state: IState) {
  return {
    relations: state.mks.relations.data,
    selectedDomain: state.mks.selectedDomain,
  };
}

const connector = connect(mapStateToProps);
type IReduxProps = ConnectedProps<typeof connector>;

export interface IMksCylinderFormProps extends WithStyles<typeof styles>, RouteComponentProps {
  cylinderData?: ICylinderInDTO | undefined;
  receivedHostnameFromMksRequestForm?: string | undefined;
  hideDuplicateButton?: boolean;
}

export interface IMksCylinderFormState {
  isLoadingRelations: boolean;
  rowsPerPage: number;
  keyFunctionFilter: string;
  keyDesignationFilter: string;
  allRelations: IMechanicalCylinderKeyRelationInDTO[];
  filteredRelations: IMechanicalCylinderKeyRelationInDTO[];
  currentPage: number;
}

export class MksCylinderForm extends React.Component<IMksCylinderFormProps & IReduxProps, IMksCylinderFormState> {

  constructor(props: IMksCylinderFormProps & IReduxProps) {
    super(props);
    this.state = {
      isLoadingRelations: false,
      rowsPerPage: 100,
      keyFunctionFilter: '',
      keyDesignationFilter: '',
      allRelations: [],
      filteredRelations: [],
      currentPage: 0,
    };
  }

  public async componentDidMount() {
    await this.fetchAllRelations();
  }

  private async fetchAllRelations() {
    const { cylinderData, selectedDomain, receivedHostnameFromMksRequestForm, dispatch } = this.props;
    
    if (!cylinderData || (!selectedDomain && !receivedHostnameFromMksRequestForm)) return;

    try {
      this.setState({ isLoadingRelations: true });
      
      const params: IMechanicalCylinderKeyRelationsRequestParamsDTO = {
        hostname: receivedHostnameFromMksRequestForm ? receivedHostnameFromMksRequestForm : selectedDomain.value.hostname,
        cylinderIds: [cylinderData.id],
        page: 0,
        pageSize: 10000,
      };
      const result = await dispatch<any>(MksActions.fetchMechanicalCylinderKeyRelations(params));
      this.setState({ 
        allRelations: result.content || [],
        filteredRelations: result.content || [],
        isLoadingRelations: false 
      });
    } catch (error) {
      this.setState({ isLoadingRelations: false });
    }
  }

  private filterRelations = (keyFunction: string, keyDesignation: string) => {
    const { allRelations } = this.state;
    let filtered = [...allRelations];

    if (keyFunction.trim()) {
      filtered = filtered.filter(relation => 
        relation.key.keyFunction?.toLowerCase().includes(keyFunction.toLowerCase())
      );
    }

    if (keyDesignation.trim()) {
      filtered = filtered.filter(relation => 
        relation.key.designation?.toLowerCase().includes(keyDesignation.toLowerCase())
      );
    }

    this.setState({ 
      filteredRelations: filtered,
      keyFunctionFilter: keyFunction,
      keyDesignationFilter: keyDesignation,
      currentPage: 0
    });
  }

  public onSearchKeys = (keyFunction: string, keyDesignation: string) => {
    this.filterRelations(keyFunction, keyDesignation);
  }

  public onFetchKeysPerPage = (page: number) => {
    this.setState({ currentPage: page });
  }

  public onPageSizeChange = (pageSize: number) => {
    this.setState({ rowsPerPage: pageSize, currentPage: 0 });
  }

  public onKeyClick = async (keyData: IKeyInDTO) => {
    const { dispatch } = this.props;
    
    await dispatch<any>(ModalsActions.hideModal(`MKS_KEY_DETAILS_MODAL`));
    dispatch<any>(MksActions.setSelectedKey(keyData));
    dispatch<any>(ModalsActions.showModal(`MKS_KEY_DETAILS_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
      modalProps: {
        content: <MksKeyForm keyData={keyData} />,
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`MKS_KEY_DETAILS_MODAL`)),
      }
    }));
  }

  public onCreateDuplicateCylinderRequest = async () => {
    const { dispatch, cylinderData, selectedDomain } = this.props;
    const { history } = this.props;
    
    if (!cylinderData || !selectedDomain) return;

    // Verifica permessi
    if (!AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.MKS_CREATE_REQUEST)) {
      return;
    }

    try {
      // Ottieni il plant dal domain
      const params: IWalletPlantsRequestParamsDTO = { 
        page: 0, 
        pageSize: 1, 
        hostname: selectedDomain.value.hostname, 
        walletId: undefined 
      };
      const domains = await dispatch<any>(CreditsActions.fetchDomainsSearch(params));
      const domainsOptions = getDomainsOptions(domains) as IDomainOptionDTO[];
      
      if (domainsOptions.length === 0) {
        return;
      }

      const plantOption = domainsOptions[0];
      
      // Prepara i dati iniziali - converti ICylinderInDTO in IMKSRequestDataCylinder
      const cylinderForRequest = {
        id: cylinderData.id,
        externalId: cylinderData.externalId,
        doorName: cylinderData.doorName || '',
        marking: cylinderData.marking || '',
        type: cylinderData.type || ''
      };
      
      // Nuovo formato: usa cylindersDuplicates array invece di cylinder singolo
      const initialData: { [key: string]: string } = {
        cylindersDuplicates: JSON.stringify([{ 
          cylinder: cylinderForRequest, 
          quantity: cylinderData.quantity || 1 
        }]),
      };

      // Chiudi il modal corrente e apri il form di richiesta
      await dispatch<any>(ModalsActions.hideModal(`MKS_CYLINDER_DETAILS_MODAL`));
      
      dispatch<any>(ModalsActions.showModal(`MKS_REQUEST_FORM_VIEW`, {
        modalType: ModalTypes.OPERATIONAL_VIEW_MODAL_BACKDROP,
        modalProps: {
          content: (
            <MKSRequestForm
              initialRequestType={MKSRequestTypeOptions.CYLINDER_DUPLICATION}
              initialPlant={plantOption}
              initialData={initialData}
              onCreateEditMKSRequest={async (request) => {
                try {
                  await dispatch<any>(MksActions.createMKSRequest(request as any));
                  dispatch<any>(ModalsActions.hideModal(`MKS_REQUEST_FORM_VIEW`));
                  if (history.location.pathname!=='/mksRequests') {
                    history.push('/mksRequests')
                    history.go(0)
                  }
                } catch (error) {
                  console.error('Error creating request:', error);
                }
              }}
              onCreateAndSendRequest={async (request) => {
                try {
                  const createdRequest = await dispatch<any>(MksActions.createMKSRequest(request));
                  if (createdRequest && createdRequest.id) {
                    const statusChangeData: IMKSRequestStatusChangeDTO = {
                      status: MKSRequestStatusOptions.SENT
                    };
                    await dispatch<any>(MksActions.changeMKSRequestStatus(createdRequest.id, statusChangeData));
                  }
                  dispatch<any>(ModalsActions.hideModal(`MKS_REQUEST_FORM_VIEW`));
                  if (history.location.pathname!=='/mksRequests') {
                    history.push('/mksRequests')
                    history.go(0)
                  }
                } catch (error) {
                  console.error('Error creating and sending request:', error);
                }
              }}
            />
          ),
          titleMessageKey: 'mks.newRequest',
        }
      }));
    } catch (error) {
      console.error('Error creating duplicate cylinder request:', error);
    }
  }

  public render() {
    const { classes, cylinderData, hideDuplicateButton } = this.props;
    const { isLoadingRelations, rowsPerPage, filteredRelations, currentPage } = this.state;

    if (!cylinderData) {
      return (
        <div className={classes.container}>
          <ReduxLanguage languageKey="mks.noCylinderSelected" />
        </div>
      );
    }
    
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedRelations = filteredRelations.slice(startIndex, endIndex);
    
    
    const relationsData: PaginatedDataDTO<IMechanicalCylinderKeyRelationInDTO> = {
      content: paginatedRelations,
      pagination: {
        number: currentPage,
        size: rowsPerPage,
        totalElements: filteredRelations.length,
        totalPages: Math.ceil(filteredRelations.length / rowsPerPage)
      }
    };

    const hasPermission = AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.MKS_CREATE_REQUEST);

    return (
      <div className={classes.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <Typography variant="h6" className={classes.sectionTitle}>
            <ReduxLanguage languageKey="mks.cylinderDetails" />
          </Typography>
          {hasPermission && !hideDuplicateButton && (
            <Button
              variant="contained"
              className={classes.actionButton}
              style={{ backgroundColor: '#5AC0B1', flexShrink: 0 }}
              onClick={this.onCreateDuplicateCylinderRequest}
            >
              <AddIcon className={classes.icon} />
              <h5 className={classes.actionButtonText}><ReduxLanguage languageKey="mks.createDuplicateCylinderRequest" /></h5>
            </Button>
          )}
        </div>
        <div style={{ backgroundColor: '#f0f0f0', padding: 10, borderRadius: 10 }}>
          <table width="100%" style={{ marginBottom: 20 }}>
            <tbody>
              <tr>
                <td style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
                    <ReduxLanguage languageKey="mks.id" />
                  </h3>
                  <h3 style={{ margin: 0, fontWeight: 'normal', fontSize: '16px' }}>
                    {cylinderData.id || '---'}
                  </h3>
                </td>
                <td style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
                    <ReduxLanguage languageKey="mks.externalId" />
                  </h3>
                  <h3 style={{ margin: 0, fontWeight: 'normal', fontSize: '16px' }}>
                    {cylinderData.externalId || '---'}
                  </h3>
                </td>
                <td style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  {/* Empty cell for alignment */}
                </td>
              </tr>
              <tr>
                <td colSpan={3} style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
                    <ReduxLanguage languageKey="mks.doorName" />
                  </h3>
                  <h3 style={{ margin: 0, fontWeight: 'normal', fontSize: '16px' }}>
                    {cylinderData.doorName || '---'}
                  </h3>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
                    <ReduxLanguage languageKey="mks.marking" />
                  </h3>
                  <h3 style={{ margin: 0, fontWeight: 'normal', fontSize: '16px' }}>
                    {cylinderData.marking || '---'}
                  </h3>
                </td>
                <td style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
                    <ReduxLanguage languageKey="mks.type" />
                  </h3>
                  <h3 style={{ margin: 0, fontWeight: 'normal', fontSize: '16px' }}>
                    {cylinderData.type || '---'}
                  </h3>
                </td>
                <td style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  {/* Empty cell for alignment */}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
                    <ReduxLanguage languageKey="mks.insideLength" />
                  </h3>
                  <h3 style={{ margin: 0, fontWeight: 'normal', fontSize: '16px' }}>
                    {cylinderData.insideLength || '---'}
                  </h3>
                </td>
                <td style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
                    <ReduxLanguage languageKey="mks.outsideLength" />
                  </h3>
                  <h3 style={{ margin: 0, fontWeight: 'normal', fontSize: '16px' }}>
                    {cylinderData.outsideLength || '---'}
                  </h3>
                </td>
                <td style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  {/* Empty cell for alignment */}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
                    <ReduxLanguage languageKey="mks.quantity" />
                  </h3>
                  <h3 style={{ margin: 0, fontWeight: 'normal', fontSize: '16px' }}>
                    {cylinderData.quantity || '---'}
                  </h3>
                </td>
                <td colSpan={2} style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  {/* Empty cells for alignment */}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <Divider style={{ margin: '0px 0' }} />
        <Typography variant="h6" className={classes.sectionTitle}>
          <ReduxLanguage languageKey="mks.associatedKeys" />
        </Typography>

        <AssociatedKeysTable
          relations={relationsData}
          onFetchElementsOnPage={this.onFetchKeysPerPage}
          onPageSizeChange={this.onPageSizeChange}
          onSearch={this.onSearchKeys}
          onRowClick={this.onKeyClick}
          rowsPerPage={rowsPerPage}
          isLoading={isLoadingRelations}
          useClientSideFiltering={true}
        />
      </div>
    );
  }
}

export default connector(withRouter(withStyles(styles)(MksCylinderForm)));
